import React from 'react';
import { is, isEmpty } from 'ramda';
import { isBlank } from 'utils/helperMethods';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Box, Typography, TextField, InputAdornment } from '@material-ui/core';

import AddIcon from '@material-ui/icons/AddCircleOutlineOutlined';
import MinusIcon from '@material-ui/icons/RemoveCircleOutlineOutlined';
import useScreen from 'hooks/useScreen';
import Slider from 'sharedComponents/Slider';
import CustomNumberInput from './components/CustomNumberInput';

import useStyles from './useStyles';

const MOBILE_PROGRESS_MARKS_STEPS = 4;
const DESKTOP_PROGRESS_MARKS_STEPS = 4;

const normalizeValue = (value, min, max) => {
  if (value > max) {
    return max;
  }
  if (value < min) {
    return min;
  }
  return value;
};

const InputWithSlider = props => {
  const { onChange, value, min, max, label, className, marksDivider, suffix, prefix, strictLimit } = props;
  const classes = useStyles();
  const { isDesktop } = useScreen();
  const sliderStep = () => {
    let sliderStepValue;
    if (max > 20000) {
      sliderStepValue = 1000;
    } else if (max > 50) {
      sliderStepValue = 100;
    } else {
      sliderStepValue = 1;
    }
    return sliderStepValue;
  };
  const marksSteps = isDesktop ? DESKTOP_PROGRESS_MARKS_STEPS : MOBILE_PROGRESS_MARKS_STEPS;

  const buildMarks = () => {
    const step = (max - min) / marksSteps;
    const marks = [];
    for (let i = 0; i <= marksSteps; i += 1) {
      const currentValue = min + i * step;
      const markValue =
        currentValue >= 1000000 ? (currentValue / 1000000).toFixed(1) : (currentValue / marksDivider).toFixed(0);
      let formattedSuffix;
      if (currentValue >= 1000000) {
        formattedSuffix = 'mm';
      } else if (marksDivider === 1000 && markValue !== 0) {
        formattedSuffix = 'k';
      } else {
        formattedSuffix = suffix;
      }
      const markLabel = isBlank(suffix)
        ? `${prefix}${markValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${formattedSuffix}`
        : `${markValue}${formattedSuffix}`;
      marks.push({ value: currentValue, label: markLabel });
    }
    return marks;
  };

  const handleSliderChange = (_event, newValue) => {
    onChange(newValue);
  };

  const handleInputChange = ({ target: { value: newValue } }) => {
    if (newValue === '') {
      onChange(newValue);
      return;
    }
    const numberValue = Number(newValue);
    const normalizedValue = strictLimit ? normalizeValue(numberValue, min, max) : numberValue;
    onChange(normalizedValue);
  };

  const handleIncrementValue = () => {
    if (typeof value !== 'number' || value < sliderStep()) {
      onChange(sliderStep());
      return;
    }
    const incrementalValue = value + sliderStep();
    const newValue = strictLimit ? normalizeValue(incrementalValue, min, max) : incrementalValue;
    onChange(newValue);
  };

  const handleDecrementValue = () => {
    if (typeof value !== 'number' || value < sliderStep()) {
      onChange(0);
      return;
    }

    const decrementalValue = value - sliderStep();
    const newValue = strictLimit ? normalizeValue(decrementalValue, min, max) : decrementalValue;
    onChange(newValue);
  };

  const marks = buildMarks();

  return (
    <Box className={clsx(className, classes.container)}>
      <Box display="flex" flexDirection="column">
        <Typography variant="subtitle1" color="secondary">
          {label}
        </Typography>
        <TextField
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MinusIcon className={classes.iconButton} onClick={handleDecrementValue} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <AddIcon className={classes.iconButton} onClick={handleIncrementValue} />
              </InputAdornment>
            ),
            inputComponent: CustomNumberInput,
          }}
          onChange={handleInputChange}
          value={value}
          variant="outlined"
          className={classes.field}
          name="numberformat"
          id="formatted-numberformat-input"
        />
      </Box>
      <Box className={classes.slider}>
        <Slider
          min={min}
          max={max}
          value={is(String, value) && isEmpty(value) ? min : Number(value)}
          onChange={handleSliderChange}
          step={sliderStep()}
          marks={marks}
        />
      </Box>
    </Box>
  );
};

InputWithSlider.propTypes = {
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number.isRequired,
  className: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  strictLimit: PropTypes.bool,
};

InputWithSlider.defaultProps = {
  label: '',
  prefix: '$',
  suffix: '',
  min: 0,
  value: null,
  className: '',
  strictLimit: false,
};

export default InputWithSlider;



// WEBPACK FOOTER //
// ./src/sharedComponents/InputWithSlider/InputWithSlider.js